import React from 'react';
import { UserProfile, FinancialConfig, Quote, Material, ProductTemplate, SimpleService, Client, AppView, ProfileStep } from '../../types';
import ReverseOnboarding from '../ReverseOnboarding';
import PreQuoteReview from '../PreQuoteReview';
import Onboarding from '../Onboarding';
import Home from '../Home';
import Dashboard from '../Dashboard';
import CostCenter from '../CostCenter';
import ProfileEditor from '../ProfileEditor';
import QuoteCreator from '../QuoteCreator';
import RegionComparison from '../RegionComparison';
import QuoteHistory from '../QuoteHistory';
import ErrorBoundary from '../ErrorBoundary';
import { CostCenterTab } from '../../hooks/useCostCenterTabs';
import { useTeamPermissions } from '../../contexts/TeamPermissionsContext';
import { Lock } from 'lucide-react';
import { PaywallInitialPlan } from '../PaywallModal';
import BatchTaxSimulatorPage from '../../pages/BatchTaxSimulatorPage';

export interface CoreViewDispatcherProps {
  view: AppView;
  user: UserProfile | null;
  config: FinancialConfig | null;
  quotes: Quote[];
  materials: Material[];
  recipes: ProductTemplate[];
  services: SimpleService[];
  clients: Client[];
  isFirstVisit: boolean;
  profileStep?: ProfileStep;
  costCenterInitialTab: CostCenterTab;
  initialQuoteDraft: any;
  tempNiche: string;
  tempService: string;
  currentMonthQuotesCount: number;
  isActivatingTrial: boolean;
  isEnterpriseUser?: boolean;
  renderWrapper?: (content: React.ReactNode) => React.ReactNode;
  onGoBack: () => void;
  onOpenChat: () => void;
  onOpenDashboard: () => void;
  onOpenProfile: (step?: ProfileStep) => void;
  onOpenMaterials: () => void;
  onOpenErpHub: () => void;
  onOpenPlatformManagement: () => void;
  onOpenReformaLote?: () => void;
  onOpenTour?: () => void;
  onOpenComparison: () => void;
  onOpenHistory: () => void;
  onOpenEstoqueHub: () => void;
  onStartTrial: () => void;
  onOpenPaywall: (plan?: PaywallInitialPlan) => void;
  onLogout: () => Promise<void>;
  onSaveQuote: (quote: Quote) => Promise<any>;
  onUpdateQuoteStatus: (quoteId: string, status: any) => Promise<void>;
  onReuseQuote: (quote: Quote) => void;
  onSaveMaterials: (m: Material[]) => Promise<void>;
  onSaveRecipe: (r: ProductTemplate) => Promise<any>;
  onDeleteRecipe: (id: string) => Promise<void>;
  onSaveService: (s: SimpleService) => Promise<any>;
  onDeleteService: (id: string) => Promise<void>;
  onProfileUpdate: (user: UserProfile, config: FinancialConfig) => Promise<void>;
  onResetNicheData: () => Promise<void>;
  onReverseOnboardingComplete: (niche: string, service: string) => void;
  onPreQuoteComplete: (user: UserProfile, config: FinancialConfig, materialCost: number, timeSpent: number) => void;
  onOnboardingComplete: (user: UserProfile, config: FinancialConfig, redirectView?: AppView) => void;
  onOnboardingWizardComplete: (hourlyRate: number, fixedCost: number, proLabore: number) => Promise<void>;
  onClearInitialQuoteDraft: () => void;
}

export const CoreViewDispatcher: React.FC<CoreViewDispatcherProps> = ({
  view,
  user,
  config,
  quotes,
  materials,
  recipes,
  services,
  clients,
  isFirstVisit,
  profileStep,
  costCenterInitialTab,
  initialQuoteDraft,
  tempNiche,
  tempService,
  currentMonthQuotesCount,
  isActivatingTrial,
  isEnterpriseUser,
  renderWrapper,
  onGoBack,
  onOpenChat,
  onOpenDashboard,
  onOpenProfile,
  onOpenMaterials,
  onOpenErpHub,
  onOpenPlatformManagement,
  onOpenReformaLote,
  onOpenTour,
  onOpenComparison,
  onOpenHistory,
  onOpenEstoqueHub,
  onStartTrial,
  onOpenPaywall,
  onLogout,
  onSaveQuote,
  onUpdateQuoteStatus,
  onReuseQuote,
  onSaveMaterials,
  onSaveRecipe,
  onDeleteRecipe,
  onSaveService,
  onDeleteService,
  onProfileUpdate,
  onResetNicheData,
  onReverseOnboardingComplete,
  onPreQuoteComplete,
  onOnboardingComplete,
  onOnboardingWizardComplete,
  onClearInitialQuoteDraft,
}) => {
  const { can } = useTeamPermissions();
  const wrap = (node: React.ReactNode) => (isEnterpriseUser && renderWrapper ? renderWrapper(node) : node);

  const effectiveConfig: FinancialConfig = {
    userId: config?.userId || user?.id || 'default',
    proLaboreGoal: config?.proLaboreGoal || 3000,
    monthlyFixedCost: config?.monthlyFixedCost || 1500,
    weeklyWorkHours: config?.weeklyWorkHours || 40,
    baseHourValue: (config?.baseHourValue && config.baseHourValue > 0) ? config.baseHourValue : 25,
    paymentFeePercentage: config?.paymentFeePercentage || 0,
    defaultCommissionPercentage: config?.defaultCommissionPercentage || 0,
    logoUrl: config?.logoUrl
  };

  if (view === 'reverse-onboarding') {
    return <ReverseOnboarding onComplete={onReverseOnboardingComplete} />;
  }

  if (view === 'pre-quote') {
    return (
      <PreQuoteReview
        nicheName={tempNiche}
        serviceDescription={tempService}
        onComplete={onPreQuoteComplete}
      />
    );
  }

  if (view === 'onboarding') {
    return (
      <Onboarding
        onComplete={onOnboardingComplete}
        initialUser={user}
        initialConfig={effectiveConfig}
      />
    );
  }


  if (view === 'home' && user) {
    return (
      <Home
        user={user}
        isFirstVisit={isFirstVisit}
        quotesCount={quotes.length}
        materialsCount={materials.length + services.length}
        onOpenChat={onOpenChat}
        onOpenDashboard={onOpenDashboard}
        onOpenProfile={onOpenProfile}
        onOpenMaterials={onOpenMaterials}
        onOpenErpHub={onOpenErpHub}
        onOpenPlatformManagement={onOpenPlatformManagement}
        onOpenTour={onOpenTour}
        onStartTrial={onStartTrial}
        onOpenPaywall={(plan) => {
          onOpenPaywall(plan || (user?.plan === 'Pro' ? 'specialist' : 'annual'));
        }}
        isActivatingTrial={isActivatingTrial}
      />
    );
  }

  if (view === 'dashboard' && user) {
    return (
      <Dashboard
        user={user}
        config={effectiveConfig}
        quotes={quotes}
        materials={materials}
        onUpdateQuoteStatus={onUpdateQuoteStatus}
        onEditProfile={onOpenProfile}
        onOpenComparison={onOpenComparison}
        onOpenHistory={onOpenHistory}
        onOpenMaterials={onOpenMaterials}
        onBack={onGoBack}
        onReuseQuote={onReuseQuote}
      />
    );
  }

  if (view === 'costs' && user) {
    if (!can('view_cost_center')) {
      return (
        <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4 font-sans">
          <div className="max-w-md w-full bg-white border border-zinc-200 rounded-3xl p-6 text-center space-y-4 shadow-xl">
            <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 mx-auto flex items-center justify-center border border-amber-200">
              <Lock className="w-6 h-6" />
            </div>
            <h3 className="text-base font-black text-zinc-900">Acesso Restrito pelo Gestor</h3>
            <p className="text-xs text-zinc-500 leading-relaxed">
              O acesso ao Centro de Custos e Parâmetros Financeiros é restrito pelo usuário Master da empresa.
            </p>
            <button
              type="button"
              onClick={onGoBack}
              className="w-full py-2.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 font-bold text-xs rounded-xl transition-colors cursor-pointer"
            >
              Voltar
            </button>
          </div>
        </div>
      );
    }
    return wrap(
      <CostCenter
        user={user}
        config={effectiveConfig}
        materials={materials}
        recipes={recipes}
        services={services}
        onSaveMaterials={onSaveMaterials}
        onSaveRecipe={onSaveRecipe}
        onDeleteRecipe={onDeleteRecipe}
        onSaveService={onSaveService}
        onDeleteService={onDeleteService}
        onBack={onGoBack}
        initialTab={costCenterInitialTab}
        onNavigateToStock={onOpenEstoqueHub}
      />
    );
  }

  if (view === 'profile' && user) {
    return wrap(
      <ProfileEditor
        user={user}
        config={effectiveConfig}
        materials={materials}
        onSave={onProfileUpdate}
        onResetNicheData={onResetNicheData}
        onClose={onGoBack}
        onLogout={onLogout}
        initialStep={profileStep}
      />
    );
  }

  if (view === 'chat' && user) {
    return (
      <ErrorBoundary onReset={onGoBack}>
        <QuoteCreator
          user={user}
          config={effectiveConfig}
          materials={materials}
          recipes={recipes}
          services={services}
          clients={clients}
          onClose={onGoBack}
          onSaveQuote={onSaveQuote}
          onOpenHistory={onOpenHistory}
          onOpenProfile={onOpenProfile}
          onOpenCosts={onOpenMaterials}
          quotesCount={currentMonthQuotesCount}
          plan={user.plan || 'Grátis'}
          initialData={initialQuoteDraft}
          onClearInitialData={onClearInitialQuoteDraft}
        />
      </ErrorBoundary>
    );
  }

  if (view === 'comparison' && user) {
    return (
      <RegionComparison
        user={user}
        config={effectiveConfig}
        quotes={quotes}
        onClose={onGoBack}
      />
    );
  }

  if (view === 'history' && user) {
    return wrap(
      <QuoteHistory
        quotes={quotes}
        materials={materials}
        user={user}
        onBack={onGoBack}
        onUpdateQuoteStatus={onUpdateQuoteStatus}
        onReuseQuote={onReuseQuote}
      />
    );
  }

  if (view === 'reforma_lote' && user) {
    return wrap(
      <BatchTaxSimulatorPage
        embeddedInPortal={true}
        user={user}
        materials={materials}
        services={services}
        onSaveMaterials={onSaveMaterials}
        onSaveService={onSaveService}
        onBack={onGoBack}
      />
    );
  }

  // Fallback seguro anti-tela-branca: se usuário existe, exibe Home
  if (user) {
    return (
      <Home
        user={user}
        isFirstVisit={isFirstVisit}
        onOpenChat={onOpenChat}
        onOpenDashboard={onOpenDashboard}
        onOpenProfile={onOpenProfile}
        onOpenMaterials={onOpenMaterials}
        onOpenErpHub={onOpenErpHub}
        onOpenPlatformManagement={onOpenPlatformManagement}
        onOpenReformaLote={onOpenReformaLote}
        onStartTrial={onStartTrial}
        onOpenPaywall={(plan) => {
          onOpenPaywall(plan || (user?.plan === 'Pro' ? 'specialist' : 'annual'));
        }}
        isActivatingTrial={isActivatingTrial}
      />
    );
  }

  return null;
};
